// ─────────────────────────────────────────────────────────────────────────────
// Typed API client for the AI Playground Worker backend
// ─────────────────────────────────────────────────────────────────────────────

import type {
  ModelsListResponse,
  ModelInfo,
  RunResponse,
  HistoryListResponse,
  HistoryEntry,
  FavoritesListResponse,
  FavoriteEntry,
  UploadResponse,
  ChatMessage,
} from '@shared/types';

// Read from Vite env or fall back to same-origin (dev proxy handles /api)
export const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';;

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const err = await res.json() as { message?: string };
      message = err.message ?? message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

// ─── Model Schema ────────────────────────────────────────────────────────────

export interface JSONSchemaProperty {
  type?: string | string[];
  items?: JSONSchemaProperty;
  description?: string;
  minimum?: number;
  maximum?: number;
  default?: unknown;
  enum?: unknown[];
  contentEncoding?: string;
  contentMediaType?: string;
  format?: string;
  properties?: Record<string, JSONSchemaProperty>;
  required?: string[];
  oneOf?: JSONSchemaProperty[];
  maxLength?: number;
  minLength?: number;
}

export interface ModelSchemaResponse {
  result: {
    input: JSONSchemaProperty;
    output: JSONSchemaProperty;
  };
  success: boolean;
  errors: unknown[];
  messages: unknown[];
}

export const schemaApi = {
  get(modelId: string): Promise<ModelSchemaResponse> {
    return request<ModelSchemaResponse>(`/api/models/schema?model=${encodeURIComponent(modelId)}`);
  },
};

// ─── Models ───────────────────────────────────────────────────────────────────

export const modelsApi = {
  list(params?: { task?: string; search?: string; source?: 'static' | 'live' }): Promise<ModelsListResponse> {
    const qs = new URLSearchParams();
    if (params?.task) qs.set('task', params.task);
    if (params?.search) qs.set('search', params.search);
    if (params?.source) qs.set('source', params.source);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return request<ModelsListResponse>(`/api/models${query}`);
  },

  get(id: string): Promise<ModelInfo> {
    return request<ModelInfo>(`/api/models/${encodeURIComponent(id)}`);
  },

  tasks(): Promise<{ tasks: Array<{ id: string; label: string; count: number }> }> {
    return request('/api/tasks');
  },
};

// ─── Inference ────────────────────────────────────────────────────────────────

export const inferenceApi = {
  /** Single-shot inference */
  run(model: string, inputs: Record<string, unknown>): Promise<RunResponse> {
    return request<RunResponse>('/api/run', {
      method: 'POST',
      body: JSON.stringify({ model, inputs }),
    });
  },

  /** Chat inference */
  chat(params: {
    model: string;
    messages: ChatMessage[];
    temperature?: number;
    top_p?: number;
    max_tokens?: number;
    seed?: number;
  }): Promise<RunResponse> {
    return request<RunResponse>('/api/chat', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  /**
   * Streaming inference via SSE.
   * Returns an EventSource-like async generator that yields delta strings.
   */
  async *stream(params: {
    model: string;
    messages?: ChatMessage[];
    prompt?: string;
    temperature?: number;
    top_p?: number;
    max_tokens?: number;
    seed?: number;
    image?: number[];
  }): AsyncGenerator<{ type: 'delta'; text: string } | { type: 'done'; timing: { total_ms: number } } | { type: 'error'; error: string }> {
    const res = await fetch(`${BASE_URL}/api/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!res.ok || !res.body) {
      throw new Error(`Stream request failed: HTTP ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          if (trimmed.startsWith('event: done')) continue;
          if (trimmed.startsWith('event: error')) continue;

          if (trimmed.startsWith('data: ')) {
            const dataStr = trimmed.slice(6);
            if (dataStr === '[DONE]') {
              yield { type: 'done', timing: { total_ms: 0 } };
              return;
            }

            try {
              const parsed = JSON.parse(dataStr) as Record<string, unknown>;

              // Handle done event
              if (parsed['timing']) {
                yield { type: 'done', timing: parsed['timing'] as { total_ms: number } };
                return;
              }

              // Handle error event
              if (parsed['error']) {
                yield { type: 'error', error: parsed['error'] as string };
                return;
              }

              // Extract delta text from Workers AI SSE format
              // Workers AI wraps: { response: "text" } or OpenAI-compat { choices: [{ delta: { content } }] }
              const response = parsed['response'] as string | undefined;
              const choices = parsed['choices'] as Array<{ delta?: { content?: string } }> | undefined;
              const text = response ?? choices?.[0]?.delta?.content ?? '';

              if (text) {
                yield { type: 'delta', text };
              }
            } catch {
              // Malformed SSE data – skip
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /** Upload image or audio file */
  async upload(file: File): Promise<UploadResponse & { data: string; mediaType: 'image' | 'audio' }> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json() as { message?: string };
      throw new Error(err.message ?? `Upload failed: HTTP ${res.status}`);
    }

    return res.json() as Promise<UploadResponse & { data: string; mediaType: 'image' | 'audio' }>;
  },
};

// ─── History ──────────────────────────────────────────────────────────────────

export const historyApi = {
  list(params?: { limit?: number; cursor?: string }): Promise<HistoryListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.cursor) qs.set('cursor', params.cursor);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return request<HistoryListResponse>(`/api/history${query}`);
  },

  save(entry: Omit<HistoryEntry, 'id' | 'timestamp'>): Promise<HistoryEntry> {
    return request<HistoryEntry>('/api/history', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  delete(id: string): Promise<void> {
    return request<void>(`/api/history/${id}`, { method: 'DELETE' });
  },

  clear(): Promise<{ deleted: number }> {
    return request<{ deleted: number }>('/api/history', { method: 'DELETE' });
  },
};

// ─── Favorites ────────────────────────────────────────────────────────────────

export const favoritesApi = {
  list(): Promise<FavoritesListResponse> {
    return request<FavoritesListResponse>('/api/favorites');
  },

  save(entry: Omit<FavoriteEntry, 'id' | 'createdAt'>): Promise<FavoriteEntry> {
    return request<FavoriteEntry>('/api/favorites', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  },

  delete(id: string): Promise<void> {
    return request<void>(`/api/favorites/${id}`, { method: 'DELETE' });
  },
};
